import { useMemo } from "react"
import { useSelector } from "react-redux"
import { map, chain } from "lodash"
import useResource from "hooks/resources"
import { useAccountAlias } from "resources/account"
import { computeBaseHistory } from "helpers/base"
import { combineTanStackStatuses, OK } from "helpers/data-statuses"
import { usePortfolios } from "resources/portfolios"
import { useBaskets } from "resources/baskets"
import { getRecentInstruments } from "selectors/recentInstruments"

export const RECENT_INSTRUMENT_SEARCH_TYPES = {
  BASE: "base",
  GENERAL: "general",
  EXPERIMENT: "experiment",
  DASHBOARD: "dashboard",
  COMPARISON: "comparison",
  REBALANCE: "rebalance",
  FILTER: "filter",
}

export function getRecentInstrumentsQueryUrl(account, modelId) {
  if (!modelId || !account) {
    return null
  }
  return {
    id: `recentInstruments-${modelId}`,
    url: `/api/${account}/recent-instruments?${new URLSearchParams({ modelId })}`,
  }
}

const INDEXES_QUERY = {
  resource: "indexes",
  url: () => ({
    id: "indexes",
    query: `query {
      indexes {
        id
        name
        description
        availableFrom
        terminationDate
      }
    }`,
  }),
}

const BENCHMARK_GRAPHQL_QUERY = `query ($modelId: String!) {
  model (id: $modelId) {
    benchmarks {
      id
      name
      availableFrom
    }
  }
}`

export function useIndexes() {
  return useResource(INDEXES_QUERY, "indexes")
}

export function useBenchmarks(modelId) {
  return useResource({
    resource: "modelBenchmarks",
    url: () => {
      if (!modelId) {
        return null
      }
      return {
        id: `modelBenchmarks-${modelId}`,
        query: BENCHMARK_GRAPHQL_QUERY,
        variables: { modelId },
      }
    },
  }, ["model", "benchmarks"])
}

export function useETFs(modelId) {
  const account = useAccountAlias()
  const [etfs, etfsStatus] = useResource({
    resource: "etfs",
    url: () => modelId ? `/api/${account}/m/${modelId}/etfs` : null,
  }, "etfs")

  return useMemo(() => {
    return [
      map(etfs, (etf) => ({
        id: etf.internalId,
        ...etf,
      })),
      etfsStatus,
    ]
  }, [etfs, etfsStatus])
}

export function useActiveInstruments(modelId) {
  const [etfs, etfsStatus] = useETFs(modelId)
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [baskets, basketsStatus] = useBaskets()
  const [indexes, indexesStatus] = useIndexes()
  const [benchmarks, benchmarksStatus] = useBenchmarks(modelId)

  const status = combineTanStackStatuses([etfsStatus, portfoliosStatus, basketsStatus, indexesStatus, benchmarksStatus])

  return useMemo(() => {
    let instruments = null
    if (status == OK) {
      instruments = {
        etfs,
        portfolios,
        baskets,
        indices: indexes,
        benchmarks,
      }
    }

    return [instruments, status]
  }, [etfs, portfolios, baskets, indexes, benchmarks, status])
}

export function useRecentInstrumentBaseHistory(modelId) {
  const recentUnifiedSearchInstruments = useSelector(getRecentInstruments)
  const [activeInstruments, activeInstrumentsStatus] = useActiveInstruments(modelId)

  const recentHistoryBases = useMemo(
    () => {
      if (!recentUnifiedSearchInstruments) {
        return []
      }
      return chain(recentUnifiedSearchInstruments)
        .filter(instrument => instrument.searchType === RECENT_INSTRUMENT_SEARCH_TYPES.BASE)
        .orderBy("order")
        .value()
    },
    [recentUnifiedSearchInstruments],
  )

  const baseHistory = useMemo(
    () => computeBaseHistory(recentHistoryBases, null, activeInstruments),
    [recentHistoryBases, activeInstruments],
  )

  if (activeInstrumentsStatus !== OK) {
    return [null, status]
  }

  return [baseHistory, status]
}

// Had to build these types earlier, leaving them here even if TS conversion is not done.
// interface AvailableBasket {
//   ticker: string // Ticker
//   description: string
//   availableFrom: IsoDate
//   terminationDate: IsoDate | null
//   // [TODO:TS]
//   currencyOverride: null
//   // [TODO:TS]
//   lastUpdated: null
// }

// interface AvailableBenchmark {
//   id: string
//   name: string
//   availableFrom: IsoDate
// }

// interface AvailableEtf {
//   id: string // internal id
//   internalId: string // internal id
//   description: string
//   ticker: string // Ticker
//   mic: string // MIC
//   adv: number
//   availableFrom: IsoDate
//   hasComposition: boolean
// }

// interface AvailableIndex {
//   id: string
//   name: string
//   description: string
//   availableFrom: IsoDate
//   terminationDate: IsoDate | null
// }

// interface AvailablePortfolio {
//   id: Uuid
//   name: string
//   alias: string
//   description: string
//   live: boolean
//   intradayEnabled: boolean
//   fxHedgeEnabled: boolean
//   shortId: string // [TODO] how is that different from alias?
//   modelId: string
//   // [TODO:TS] Type the following fields
//   betaSecurityId: null
//   betaResourceId: null
//   betaResourceType: null
//   betaBenchmarkId: null
//   betaIndexId: null
//   currencyOverride: null
//   lastUpdated: null
// }
